import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  signInWithPopup,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';

interface FirebaseUser {
  id?: number;
  firebase_uid: string;
  email: string;
  display_name: string;
  photo_url?: string;
  role: string;
  is_active: string;
}

interface AuthContextType {
  currentUser: User | null;
  dbUser: FirebaseUser | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [dbUser, setDbUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  const getApiUrl = () => {
    if (import.meta.env.VITE_API_URL) {
      return import.meta.env.VITE_API_URL;
    }
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    if (hostname === 'dc.firstshield.in') {
      return `${protocol}//${hostname}/api/dc`;
    }

    return `${protocol}//${hostname}:5002/api/dc`;
  };

  const checkUserInDatabase = async (firebaseUser: User): Promise<FirebaseUser | null> => {
    try {
      const idToken = await firebaseUser.getIdToken();
      const apiUrl = getApiUrl();

      const response = await fetch(`${apiUrl}/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'x-user-email': firebaseUser.email || ''
        },
        body: JSON.stringify({
          firebase_uid: firebaseUser.uid,
          email: firebaseUser.email,
          display_name: firebaseUser.displayName,
          photo_url: firebaseUser.photoURL
        })
      });

      if (response.ok) {
        return await response.json();
      } else {
        console.error('User not authorized in database');
        return null;
      }
    } catch (error) {
      console.error('Error checking user in database:', error);
      return null;
    }
  };

  const login = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const dbUserData = await checkUserInDatabase(user);
      if (!dbUserData) {
        await signOut(auth);
        throw new Error('You are not authorized to access this application');
      }

      setDbUser(dbUserData);
      localStorage.setItem('userEmail', user.email || '');
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setDbUser(null);
      localStorage.removeItem('userEmail');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        const dbUserData = await checkUserInDatabase(user);
        setDbUser(dbUserData);
        if (dbUserData && user.email) {
          localStorage.setItem('userEmail', user.email);
        }
      } else {
        setDbUser(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    dbUser,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
